import { useForm } from "react-hook-form";

import { ErrorMessage } from "@/app/components/error-message";
import { Button } from "@/components/ui/button";
import { DialogBody, DialogFooter } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { useUpdateSession } from "@/features/session/api/update-session";
import { AttendeesListElement } from "@/features/session/components/attendees-list.element";
import { AttendeesSearchElement } from "@/features/session/components/attendees.element";
import { RoomSystemsListElement } from "@/features/session/components/room-systems-list.element";
import { RoomSystemsSearchElement } from "@/features/session/components/room-systems.element";
import {
  AddSessionFormData,
  acceptSessionSchema,
} from "@/features/session/types/add-session-schema";
import {
  transformAcceptSessionApiDataToApiData,
  transformAcceptSessionApiDataToFormData,
} from "@/features/session/utils/session-data-transformation";
import { queryClient } from "@/lib/react-query";
import { PublicSession } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { getPendingSessionsQueryKey } from "../api/get-pending-sessions";

type AcceptInvitationFormProps = {
  session: PublicSession;
  onCancel: () => void;
};

export function AcceptInvitationForm({
  session,
  onCancel,
}: AcceptInvitationFormProps) {
  const { organisation } = useOrganisation();
  const form = useForm<AddSessionFormData>({
    resolver: zodResolver(acceptSessionSchema),
    defaultValues: transformAcceptSessionApiDataToFormData(),
  });

  const {
    mutateAsync: updateSession,
    isError: updateSessionIsError,
    error: updateSessionError,
  } = useUpdateSession({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getPendingSessionsQueryKey({
          organisationId: organisation.id,
        }),
      });
      onCancel();
    },
  });

  async function handleUpdateSession(data: AddSessionFormData) {
    // if its recurring, ask user if they want to update all sessions or just one
    if (data.isRecurring) {
      // setIsUpdateRecurringSessionModalOpen(true);
      return;
    }

    // if its not recurring, update the session in place
    await updateSession({
      id: session.id,
      session: transformAcceptSessionApiDataToApiData({ data, session }),
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleUpdateSession)}>
        <DialogBody>
          <div className="mb-4 space-y-4">
            <div className="rounded-lg border border-border p-4">
              <p className="mb-4 text-sm font-semibold text-foreground">
                Assign Users
              </p>
              <AttendeesSearchElement />
              <AttendeesListElement />
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-lg border border-border p-4">
              <p className="mb-4 text-sm font-semibold text-foreground">
                Add Room Systems
              </p>
              <RoomSystemsSearchElement />
              <RoomSystemsListElement />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          {updateSessionIsError && <ErrorMessage error={updateSessionError} />}
          <Button
            variant="outline"
            className="ml-auto"
            onClick={onCancel}
            disabled={form.formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            isAnimated
          >
            Accept
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
